"use client";

import * as React from "react";
import { Message } from "@/lib/core/crm/types";
import { useResponsiveDrawer } from "./hooks/useResponsiveDrawer";
import { useMessageActionHandlers } from "./components";
import { useDirectMessageActions } from "@/lib/hooks/messages/useDirectMessageActions";
import { MessageDetailLoading } from "./MessageDetail/MessageDetailLoading";
import { MessageDetailError } from "./MessageDetail/MessageDetailError";
import { MessageDetailDrawer } from "./MessageDetail/MessageDetailDrawer";
import { MessageDetailSidebar } from "./MessageDetail/MessageDetailSidebar";

interface MessageDetailPanelProps {
  message: Message;
  onClose: () => void;
  className?: string;
  showActions?: boolean;
  isLoading?: boolean;
  error?: string | null;
  onCopyLink?: () => void;
  // Context control
  forceDrawer?: boolean; // Force drawer mode even on desktop
  forceRegular?: boolean; // Force regular content mode even on mobile
}

export function MessageDetailPanel({
  message,
  onClose,
  className,
  showActions = true,
  isLoading = false,
  error = null,
  onCopyLink,
  forceDrawer = false,
  forceRegular = false,
}: MessageDetailPanelProps) {
  // Get responsive drawer state
  const { isMobile, shouldUseDrawer } = useResponsiveDrawer(forceDrawer, forceRegular);

  // Initialize action handlers for mobile header actions - must be called before any early returns
  const handlers = useMessageActionHandlers(message);

  // Auto-mark-as-read when an unread message is opened. This mirrors how Gmail /
  // Outlook behave (opening an email clears its unread state at the provider).
  // Runs SILENTLY (no toast) and at most once per message id, so re-renders and
  // re-opening the same message never re-fire or spam the user. Failures are
  // swallowed (logged) — the explicit "Mark as read" menu action is the loud path.
  const { markAsRead: markAsReadDirect } = useDirectMessageActions();
  const autoMarkedIdsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (isLoading || error) return;
    const id = message?.id;
    if (!id || message.isRead) return;
    if (autoMarkedIdsRef.current.has(id)) return;
    autoMarkedIdsRef.current.add(id);
    void markAsReadDirect(message, { silent: true }).catch(() => {
      // Allow a later retry if the silent mark failed.
      autoMarkedIdsRef.current.delete(id);
    });
  }, [message, message?.id, message?.isRead, isLoading, error, markAsReadDirect]);

  // Show loading state
  if (isLoading) {
    return (
      <MessageDetailLoading
        shouldUseDrawer={shouldUseDrawer}
        className={className}
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <MessageDetailError
        error={error}
        onClose={onClose}
        shouldUseDrawer={shouldUseDrawer}
        className={className}
      />
    );
  }

  // Render drawer mode
  if (shouldUseDrawer) {
    return (
      <MessageDetailDrawer
        message={message}
        showActions={showActions}
        onCopyLink={onCopyLink}
        isMobile={isMobile}
      />
    );
  }

  // Render regular content mode (desktop sidebar)
  return (
    <MessageDetailSidebar
      message={message}
      onClose={onClose}
      className={className}
      showActions={showActions}
      onCopyLink={onCopyLink}
    />
  );
}
